import {useEffect} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {filter, take} from 'rxjs/operators';
import {defineMessages} from 'react-intl';
import {useNewDesignsEnabled} from './useNewDesignsEnabled';

export const messages = defineMessages({
  askAssistantButton: {
    id: 'support_conversations_client.chatScreen.askAssistantButton',
    description: 'Label on button which restarts the conversation',
    defaultMessage: 'Ask Assistant',
  },
  chatWithAgentEnded: {
    id: 'support_conversations_client.chatScreen.chatWithAgentEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      '{name} has left the chat. Ask Assistant a new question by typing below.',
  },
  conversationEnded: {
    id: 'support_conversations_client.chatScreen.conversationEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'You can now close this drawer or ask Assistant a new question by typing below.',
  },
  racEnded: {
    id: 'support_conversations_client.chatScreen.racEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'Call received. You can now close this drawer or ask Assistant a new question by typing below.',
  },
});

const getAgentName = (conversation: Conversation): string | undefined =>
  Object.values(conversation.participants).find((p) => p.role === 'agent')
    ?.displayName;

const getMessageForConversation = (conversation: Conversation) => {
  const {channel} = conversation.context;
  const agentName = getAgentName(conversation);

  if (channel === 'request_a_call') {
    return {message: messages.racEnded};
  } else if (agentName && channel === 'chat') {
    return {message: messages.chatWithAgentEnded, values: {name: agentName}};
  }

  return {message: messages.conversationEnded};
};

type Props = {
  conversation: Conversation;
};

export function useAssistantConversationEndedLogEvents({conversation}: Props) {
  const useNewDesigns = useNewDesignsEnabled();

  useEffect(() => {
    if (!useNewDesigns) {
      return;
    }

    const sub = conversation
      .eventsObservable()
      .pipe(
        filter((e) => e.name === 'conversation-ended'),
        take(1),
      )
      .subscribe(() => {
        const {message, values} = getMessageForConversation(conversation);
        if (!conversation.context.supressChatEndedNotice) {
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message,
            values,
          });
        }
      });

    return () => sub.unsubscribe();
  }, [conversation, useNewDesigns]);
}
